import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { navigate } from 'gatsby';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { without, map, find, isArray } from 'lodash';

import Modal from '~/components/base/Modal';
import Button from '~/components/base/Button';

import { useCart } from '~/context/CartContext';

import Loader from '~/components/Loader';

import { ModalTitle } from '~/components/modal/LocationPickerModal';
import ModalCard from '~/components/modal/ModalCard';

import useGraphQLRequest from '~/hooks/useGraphQLRequest';

import { dynamicFontSize } from '~/utilities/helpers';
import { formatPrice } from '~/utilities/products';

const CardsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 35px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-top: 0;
    }
`;

const AddOnCard = styled(ModalCard)`
    flex: 0 1 100%;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        flex: 0 1 calc(33% - 10px);
    }
    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        flex: 0 1 calc(25% - 10px);
    }
    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        flex: 0 1 calc(20% - 10px);
    }
`;

const ContinueButton = styled(Button)`
    font-size: ${dynamicFontSize(2.4, 3.4, 4.4)};
    padding: 0.73em !important;
`;

const KeepShoppingButton = styled(Button)`
    font-size: ${dynamicFontSize(2.4, 3.4, 4.4)};
    padding: 0.73em !important;
    background-color: ${({ theme }) => theme.colors.primary.white};
    border: 3px solid ${({ theme }) => theme.colors.text.black};

    &:hover,
    &:focus {
        background-color: ${({ theme }) => theme.colors.text.lightBlue};
    }
`;

const ButtonsWrapper = styled.div`
    display: flex;
    gap: 25px;
    flex-wrap: wrap;
    margin-top: 35px;

    ${ContinueButton}, ${KeepShoppingButton} {
        flex: 1 1 100%;
        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            flex: 1 1 calc(50% - 12.5px);
        }
    }
`;

const getAddOnPrice = (addOn, productLocation) => {
    return (
        find(addOn.addOnPrices, {
            priceGroup: productLocation?.priceGroup
        })?.unitAmount ?? addOn.addOnInformation.price * 100
    );
};

const AddOnsModal = ({
    open,
    setOpen,
    productId,
    productLocation,
    productDisplayName = null,
    preSelectedAddOns = null,
    cartItemId = null
}) => {
    const { addToCart, updateCartItemAddOns } = useCart();
    const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);

    useEffect(() => {
        if (isArray(preSelectedAddOns)) {
            setSelectedAddOnIds(preSelectedAddOns);
        }
    }, [preSelectedAddOns]);

    const { data, status } = useGraphQLRequest(`
        query {
            product(id: "${productId}", idType: DATABASE_ID) {
                ampId
                productInformation {
                    addOns {
                        ... on AddOn {
                            databaseId
                            title
                            ampId
                            addOnInformation {
                                price
                                shortDescription
                            }
                            addOnPrices(productId: ${productId}) {
                                productId
                                priceGroup
                                unitAmount
                            }
                        }
                    }
                }
            }       
        }
    `);

    const addOns = useMemo(() => {
        return data.product
            ? map(selectedAddOnIds, (databaseId) => {
                  const addOnData = find(
                      data.product.productInformation.addOns,
                      {
                          databaseId
                      }
                  );
                  return {
                      id: databaseId,
                      ampId: addOnData.ampId,
                      title: addOnData.title,
                      price: getAddOnPrice(addOnData, productLocation)
                  };
              })
            : [];
    }, [selectedAddOnIds, data.product, productLocation]);

    const addProductToCart = useCallback(async () => {
        await addToCart(productId, productDisplayName, productLocation, addOns);
    }, [addToCart, productId, productLocation, addOns, productDisplayName]);

    const updateAddOns = useCallback(async () => {
        await updateCartItemAddOns(cartItemId, addOns, productLocation);
    }, [cartItemId, updateCartItemAddOns, addOns, productLocation]);

    const cartAction = useCallback(
        async (navigateTo = '') => {
            const shouldNavigate = navigateTo && !cartItemId;
            if (cartItemId) {
                await updateAddOns();
            } else {
                await addProductToCart();
            }
            setOpen(false);
            if (shouldNavigate) {
                navigate(navigateTo);
            }
        },
        [addProductToCart, updateAddOns, cartItemId, setOpen]
    );

    const onChange = (e) => {
        const { value, checked } = e.target;
        if (checked) {
            const addonIds = new Set([...selectedAddOnIds, parseInt(value)]);
            setSelectedAddOnIds(Array.from(addonIds));
        } else {
            setSelectedAddOnIds(without(selectedAddOnIds, parseInt(value)));
        }
    };

    return (
        <Modal {...{ open, setOpen }} onClose={cartAction}>
            <ModalTitle>Would you like to add on extra services?</ModalTitle>
            <CardsWrapper>
                {status === 'success' ? (
                    data.product.productInformation.addOns.map((addOn) => (
                        <AddOnCard
                            key={addOn.databaseId}
                            title={addOn.title}
                            description={
                                addOn.addOnInformation.shortDescription
                            }
                            bottomText={formatPrice(
                                getAddOnPrice(addOn, productLocation)
                            )}
                            inputType="checkbox"
                            inputName="addOns"
                            value={addOn.databaseId}
                            onChange={onChange}
                            defaultChecked={preSelectedAddOns?.includes(
                                addOn.databaseId
                            )}
                        />
                    ))
                ) : (
                    <Loader fullWidth={false} />
                )}
            </CardsWrapper>
            <ButtonsWrapper>
                {cartItemId ? (
                    <ContinueButton onClick={() => cartAction('/cart')}>
                        Confirm
                    </ContinueButton>
                ) : (
                    <>
                        <KeepShoppingButton
                            onClick={() =>
                                cartAction(
                                    '/product-category/unlimited-plans/'
                                ) && setOpen(false)
                            }
                        >
                            Keep Shopping
                        </KeepShoppingButton>
                        <ContinueButton onClick={() => cartAction('/cart')}>
                            Continue to Cart
                        </ContinueButton>
                    </>
                )}
            </ButtonsWrapper>
        </Modal>
    );
};

AddOnsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    productId: PropTypes.number.isRequired,
    productLocation: PropTypes.object.isRequired,
    productDisplayName: PropTypes.string,
    preSelectedAddOns: PropTypes.array,
    cartItemId: PropTypes.string
};
export default AddOnsModal;
